import React, { useMemo } from 'react';
import { type useScrollspy } from '@sg/garnish';
import {
  CustomizationIngredientsGrid,
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  CustomizationModificationItem,
  useCustomizationContext,
  useCustomizationHelpers,
} from '@sg/shared-fe/features/customization';

import { useDietaryRestrictions } from '../../../../../../../../components';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationModificationGroup = (props: Props) => {
  const {
    actor,
    modificationGroup,
    activeStepperId,
    formattedQuantity,
    helperText,
    scrollSpy,
    resetStepperTimeout,
    setActiveStepperId,
    onAddIngredient,
    onRemoveIngredient,
  } = props;

  const getRestrictionNotice = useDietaryRestrictions({ referrer: 'ProductDetails' }); // prettier-ignore

  // ─── Context ─────────────────────────────────────────────────────────

  const {
    addIngredient,
    removeIngredient,
    sortModifications,
    checkIfCanAddMoreModifications,
    getMaxPortionsForGroup,
  } = useCustomizationHelpers({ actor, onAddIngredient, onRemoveIngredient });

  const {
    allModificationsMap,
    modifications,
    activeModificationsMap,
    initialModificationsMap,
    hasReachedMaxModifiersCounts,
  } = useCustomizationContext({ actor });

  // ─── Derived Data ─────────────────────────────────────────────────────

  const sortedModifications = useMemo(
    () => sortModifications(initialModificationsMap, modificationGroup),
    [sortModifications, initialModificationsMap, modificationGroup],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!allModificationsMap || !modifications) {
    return null;
  }

  return (
    <CustomizationIngredientsGrid.Section
      key={modificationGroup.id}
      sectionId={modificationGroup.id}
      ingredientKindName={modificationGroup.displayName}
      register={scrollSpy.helpers.target.register}
      deregister={scrollSpy.helpers.target.deregister}
      helperText={helperText}
      subheading={formattedQuantity}
    >
      <CustomizationIngredientsGrid.SectionStack>
        {sortedModifications.map((modification) => {
          const ingredientId = modification.ingredient?.id;
          const modificationEntry = ingredientId ? allModificationsMap?.get(ingredientId) : null; // prettier-ignore

          if (!modificationEntry) return null;

          const { modificationGroupName } = modificationEntry;

          const canAddMoreWithinGroup = checkIfCanAddMoreModifications(modificationGroupName); // prettier-ignore
          const canAddMore = canAddMoreWithinGroup && !hasReachedMaxModifiersCounts; // prettier-ignore
          const maxPortionsForGroup = getMaxPortionsForGroup(modificationGroupName); // prettier-ignore
          const restrictionNotice = getRestrictionNotice(modification.ingredient?.dietaryProperties); // prettier-ignore

          return (
            <CustomizationModificationItem
              key={modification.id}
              activeModificationsMap={activeModificationsMap}
              activeStepperId={activeStepperId}
              canAddMore={canAddMore}
              maxPortionsForGroup={maxPortionsForGroup}
              modification={modification}
              notice={restrictionNotice}
              onIncrement={addIngredient}
              onDecrement={removeIngredient}
              resetStepperTimeout={resetStepperTimeout}
              setActiveStepperId={setActiveStepperId}
            />
          );
        })}
      </CustomizationIngredientsGrid.SectionStack>
    </CustomizationIngredientsGrid.Section>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  modificationGroup: CustomizationMachineTypes.ProductModifierGroup;
  activeStepperId: string | null;
  helperText?: string;
  formattedQuantity?: string;
  scrollSpy: ReturnType<typeof useScrollspy>;
  onAddIngredient?: (ingredientId: string) => void;
  onRemoveIngredient?: (ingredientId: string) => void;
  resetStepperTimeout: () => void;
  setActiveStepperId: (id: string | null) => void;
};
